import { WebGLTextureUtils } from "../../../utils/webgl_texture_utils.js";
import { WebGLTextureSource } from "../../types/webgl_types.js";
import { WebGLShelfAllocator, IShelfAllocation } from "./webgl_shelf_allocator.js";

export interface IWebGLDynamicAtlasAllocation extends IShelfAllocation
{
  w: number;
  h: number;
  layer: number;
}

/**
 * Manages a single WebGL TEXTURE_2D_ARRAY atlas for one GL context.
 *
 * The pixel-placement logic is fully delegated to the injected WebGLShelfAllocator,
 * which can be shared between several WebGLDynamicAtlas instances that live in
 * different GL contexts — so all contexts always upload to the same coordinates.
 */
export class WebGLDynamicAtlas
{
  private texture   : WebGLTexture | null = null;
  private gl        : WebGL2RenderingContext | null = null;
  private allocator : WebGLShelfAllocator;
  private cache     = new Map<WebGLTextureSource, { alloc: IWebGLDynamicAtlasAllocation, ref_count: number, last_used: number }>();

  /**
   * @param atlas_size        Pixel size (width and height).
   * @param layer_count       Number of layers in TEXTURE_2D_ARRAY.
   * @param allocator  Optional external allocator.
   */
  constructor( allocator: WebGLShelfAllocator )
  {
    this.allocator = allocator;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  public init( gl: WebGL2RenderingContext ): void
  {
    if ( this.texture ) return;
    this.gl = gl;
    this.createTexture();
  }

  public dispose(): void
  {
    if ( this.gl && this.texture )
      this.gl.deleteTexture( this.texture );

    this.texture = null;
    this.gl      = null;
    this.cache.clear();
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  public allocate( source: WebGLTextureSource, width?: number, height?: number, pad: number = 2 ): IWebGLDynamicAtlasAllocation | null
  {
    if ( !this.gl || !this.texture )
      throw new Error( "[WebGLDynamicAtlas] Error: Atlas not initialized. Ensure init() is called before allocation." );

    const existing = this.cache.get( source );
    if ( existing )
    {
      existing.ref_count++;
      return existing.alloc;
    }

    const w = width ?? source.width;
    const h = height ?? source.height;

    const actual_pad = (source as any).__no_pad ? 0 : pad;
    
    let shelf_alloc = this.allocator.allocate( w, h, actual_pad );
    if ( !shelf_alloc ) 
    {
      this.evictUnusedTextures(actual_pad);
      shelf_alloc = this.allocator.allocate( w, h, actual_pad );
    }

    if ( !shelf_alloc ) 
    {
      console.error( "[WebGLDynamicAtlas] CRITICAL: Atlas overflow even after Garbage Collection! You need a larger atlas." );
      return null;
    }

    const texture_source = actual_pad > 0 ? WebGLTextureUtils.prepareCanvas( source, actual_pad ) : source;
    this.upload( texture_source as any, shelf_alloc, w + actual_pad * 2, h + actual_pad * 2, actual_pad );

    const alloc: IWebGLDynamicAtlasAllocation = { ...shelf_alloc, w, h, layer: shelf_alloc.pl };
    this.cache.set( source, { alloc, ref_count: 1, last_used: performance.now() } );

    return alloc;
  }

  public free( source: WebGLTextureSource ): void
  {
    const existing = this.cache.get( source );
    if ( !existing ) return;

    if ( existing.ref_count > 0 )
    {
      existing.ref_count--;
      if ( existing.ref_count === 0 )
      {
        existing.last_used = performance.now();
      }
    }
  }

  private evictUnusedTextures(pad: number): void
  {
    const unused = [];
    for ( const [src, data] of this.cache.entries() )
    {
      if ( data.ref_count === 0 )
      {
        unused.push( { src, data } );
      }
    }

    for ( const item of unused )
    {
      this.cache.delete( item.src );
      this.allocator.free( item.data.alloc, item.data.alloc.w, item.data.alloc.h, pad );
    }
    
    if (unused.length > 0)
      console.log(`[WebGLDynamicAtlas] Garbage Collection cleared ${unused.length} textures.`);
  }

  public clearAll(): void
  {
    this.cache.clear();
    this.allocator.reset();
  }

  public getTexture(): WebGLTexture | null
  {
    return this.texture;
  }
  
  public getStats()
  {
    let in_use = 0;
    for ( const data of this.cache.values() )
    {
      if ( data.ref_count > 0 )
        in_use++;
    }
    const stats = this.allocator.getStats();
    return {
      ...stats,
      in_use,
      cached: this.cache.size,
    };
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  private createTexture(): void
  {
    const gl = this.gl!;

    this.texture = gl.createTexture()!;
    gl.bindTexture( gl.TEXTURE_2D_ARRAY, this.texture );

    gl.texImage3D(
      gl.TEXTURE_2D_ARRAY, 0, gl.RGBA,
      this.allocator.atlas_size,
      this.allocator.atlas_size,
      this.allocator.layer_count,
      0, gl.RGBA, gl.UNSIGNED_BYTE, null
    );

    gl.texParameteri( gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S,     gl.CLAMP_TO_EDGE );
    gl.texParameteri( gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T,     gl.CLAMP_TO_EDGE );
    gl.texParameteri( gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR        );
    gl.texParameteri( gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.LINEAR        );

    gl.bindTexture( gl.TEXTURE_2D_ARRAY, null );
  }

  private upload( canvas: WebGLTextureSource, alloc: IShelfAllocation, canvas_w: number, canvas_h: number, pad: number ): void
  {
    const gl   = this.gl!;
    const size = this.allocator.atlas_size;

    // WebGL V=0 is at the bottom; our py is top-down, so we flip.
    // alloc.py is the INNER py. The padded canvas starts at alloc.py - pad.
    const start_y = alloc.py - pad;
    const gl_y = size - start_y - canvas_h;

    gl.pixelStorei( gl.UNPACK_FLIP_Y_WEBGL, true );
    gl.bindTexture( gl.TEXTURE_2D_ARRAY, this.texture );

    gl.texSubImage3D(
      gl.TEXTURE_2D_ARRAY, 0,
      alloc.px - pad, gl_y, alloc.pl,
      canvas_w, canvas_h, 1,
      gl.RGBA, gl.UNSIGNED_BYTE, canvas
    );

    gl.pixelStorei( gl.UNPACK_FLIP_Y_WEBGL, false );
    gl.bindTexture( gl.TEXTURE_2D_ARRAY, null );
  }
}
